import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {ArrowLeft, ChevronRight, Package, Search, Store,} from "lucide-react";
import {getRkIssueOrders} from "../../network/externalApi";
import {formatLongDate} from "utils/dateFormat";
import RkAccountMenu from "../../components/RkAccountMenu/RkAccountMenu";
import orderStyles from "./OutletOrders.module.scss";
import styles from "./ViewReturns.module.scss";

const asArray = (value) => (Array.isArray(value) ? value : []);

const extractIssueOrders = (raw) => {
    const response = raw?.data?.response ?? raw?.data;
    if (Array.isArray(response)) return response;

    return asArray(response?.Orders);
};

const pickOrderId = (order) => order?.OrderId

const pickOrderNumber = (order) => order?.OrderNumber ?? null;

const pickMxPoNumber = (order) => order?.MxPoNumber ?? null;

const pickOutletId = (order) => order?.OutletId ?? null;

const pickOutletName = (order) => order?.OutletName ?? "Outlet";

const pickDeliveryDate = (order) => order?.DeliveryDate ?? null;

const pickReturnStatus = (order) => order?.ReturnStatus

const pickItems = (order) =>
    asArray(
        order?.ReturnItems
    );

const toCount = (value) => {
    const number = Number(value);
    return Number.isFinite(number) && number >= 0 ? number : null;
};

const pickItemCount = (order) => {
    return pickItems(order).length;
};

const pickUnitCount = (order) => {
    return pickItems(order).reduce(
        (sum, item) =>
            sum +
            (toCount(
                item?.ReturnedCount
            ) ?? 0),
        0
    );
};

const humanizeStatus = (value) => {
    const words = String(value || "")
        .trim()
        .toLowerCase()
        .replace(/[_-]+/g, " ");
    return words
        ? `${words.charAt(0).toUpperCase()}${words.slice(1)}`
        : "Return initiated";
};

const statusMeta = (order) => {
    const status = String(pickReturnStatus(order)).toUpperCase();

    if (["PROCESSED", "COMPLETED", "RETURN_PROCESSED"].includes(status)) {
        return {label: "Return processed", className: styles.statusProcessed};
    }

    if (
        ["UNDER_PROCESS", "IN_PROCESS", "IN_PROGRESS", "PROCESSING"].includes(
            status
        )
    ) {
        return {label: "Under process", className: styles.statusUnderProcess};
    }

    if (
        ["CREATED", "INITIATED", "RETURN_INITIATED", "RETURN_MARKED"].includes(
            status
        )
    ) {
        return {label: "Return initiated", className: styles.statusInitiated};
    }

    return {
        label: humanizeStatus(status),
        className: styles.statusInitiated,
    };
};

const SEARCH_DEBOUNCE_MS = 300;

const ViewReturns = () => {
    const navigate = useNavigate();
    const [keyword, setKeyword] = useState("");
    const [orders, setOrders] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        let cancelled = false;
        const trimmed = keyword.trim();

        const timer = setTimeout(async () => {
            setIsLoading(true);
            setError("");
            try {
                const response = await getRkIssueOrders(
                    trimmed ? {outletName: trimmed} : {}
                );
                if (!cancelled) setOrders(extractIssueOrders(response));
            } catch (requestError) {
                if (cancelled) return;
                setError(
                    requestError?.response?.data?.message ||
                    requestError?.message ||
                    "Failed to load returns"
                );
                setOrders([]);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        }, SEARCH_DEBOUNCE_MS);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [keyword]);

    return (
        <div className={styles.wrapper}>
            <div className={styles.header}>
                <button
                    type="button"
                    className={styles.iconButton}
                    onClick={() => navigate(-1)}
                    aria-label="Go back"
                >
                    <ArrowLeft size={18}/>
                </button>
                <h1 className={styles.title}>View return</h1>
                <RkAccountMenu buttonClassName={styles.iconButton}/>
            </div>

            <div className={styles.content}>
                <label className={styles.searchRow}>
          <span className={styles.searchIcon} aria-hidden="true">
            <Search size={18}/>
          </span>
                    <span className={styles.srOnly}>Search returns</span>
                    <input
                        className={styles.searchInput}
                        type="search"
                        placeholder="Search by train name"
                        value={keyword}
                        onChange={(event) => setKeyword(event.target.value)}
                    />
                </label>

                {isLoading ? (
                    <div className={styles.stateText}>Loading returns…</div>
                ) : error ? (
                    <div className={`${styles.stateText} ${styles.errorText}`}>
                        {error}
                    </div>
                ) : orders.length === 0 ? (
                    <div className={styles.stateText}>
                        {keyword.trim() ? "No matching returns found" : "No returns found"}
                    </div>
                ) : (
                    <div className={styles.orderList}>
                        {orders.map((order, index) => {
                            const orderId = pickOrderId(order);
                            const orderNumber = pickOrderNumber(order);
                            const mxPoNumber = pickMxPoNumber(order);
                            const outletId = pickOutletId(order);
                            const outletName = pickOutletName(order);
                            const deliveryDate = pickDeliveryDate(order);
                            const itemCount = pickItemCount(order);
                            const unitCount = pickUnitCount(order);
                            const meta = statusMeta(order);

                            return (
                                <div
                                    key={orderId ?? orderNumber ?? index}
                                    className={orderStyles.orderCard}
                                >
                                    <div className={orderStyles.orderTop}>
                                        <div className={orderStyles.orderHeaderContent}>
                      <span
                          className={`${orderStyles.statusPill} ${meta.className}`}
                      >
                        {meta.label}
                      </span>
                                            <div className={orderStyles.orderDate}>{outletName}</div>
                                            <div className={orderStyles.orderDay}>
                                                {formatLongDate(deliveryDate) || deliveryDate || "—"}
                                            </div>

                                            {orderNumber != null || mxPoNumber != null ? (
                                                <div className={orderStyles.orderIdentifiers}>
                                                    {orderNumber != null ? (
                                                        <span>
                              Order number: <strong>{orderNumber}</strong>
                            </span>
                                                    ) : null}
                                                    {mxPoNumber != null ? (
                                                        <span>
                              MX PO number: <strong>{mxPoNumber}</strong>
                            </span>
                                                    ) : null}
                                                </div>
                                            ) : null}
                                        </div>

                                        {orderId != null ? (
                                            <button
                                                type="button"
                                                className={orderStyles.orderChevron}
                                                aria-label={`View return for ${outletName}`}
                                                onClick={() =>
                                                    navigate(`/rk/view-return/${orderId}`, {
                                                        state: {
                                                            order,
                                                            orderId,
                                                            outletId,
                                                            outletName,
                                                            orderDate: deliveryDate,
                                                        },
                                                    })
                                                }
                                            >
                                                <ChevronRight size={22} strokeWidth={2.5}/>
                                            </button>
                                        ) : null}
                                    </div>

                                    <div className={orderStyles.orderDivider}/>

                                    <div className={orderStyles.orderMetaRow}>
                    <span className={orderStyles.metaIcon}>
                      <Package size={18}/>
                    </span>
                                        <span className={orderStyles.metaText}>
                      Item count:{" "}
                                            <strong>
                        {itemCount} {itemCount === 1 ? "item" : "items"}
                      </strong>
                    </span>
                                    </div>

                                    <div className={orderStyles.orderMetaRow}>
                    <span className={orderStyles.metaIcon}>
                      <Store size={18}/>
                    </span>
                                        <span className={orderStyles.metaText}>
                      Unit count:{" "}
                                            <strong>
                        {unitCount} {unitCount === 1 ? "unit" : "units"}
                      </strong>
                    </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ViewReturns;
